import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Mission from "../../../interfaces/robot/missions/mission";
import { useTranslation } from "react-i18next";
import ReadOnlyProtectedIconButton from "../../Inputs/ReadOnlyProtectedIconButton";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { getNavigatorDescription } from "../../../interfaces/robot/navigator";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Tooltip from "@mui/material/Tooltip";
import { Theme, useMediaQuery } from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import { getColorFromString } from "../../../utils/mission-color";
import ConfirmAbortDialog from "../../General/ConfirmAbortDialog";
import { useNavigate } from "react-router-dom";
import { getDefaultCleaningCommandButtonSender } from "../../../interfaces/robot/buttons/command-buttons";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { selectIsWebApp } from "../../../store/app-configurations/app-configurations-selectors";
import { selectAllCleaningZonesIdName, selectAllLandmarksIdName } from "../../../store/room/room-selectors";
import { FilterKeys } from "../../../enums/query-params/filters";
import SphereRobotTabs from "../../../enums/query-params/sphere-robot-tabs";
import MessageType from "../../../enums/robot/rsw/message-types";
import WebAppNavigationRoutes from "../../../enums/query-params/webapp-navigation-routes";

const MissionItem: React.FC<{
  mission: Mission;
  cleaningActionEnabled: boolean;
  wasAborted: boolean;
  onEdit: (mission: Partial<Mission>) => void;
  onHover: (ids: string[]) => void;
}> = ({ mission, cleaningActionEnabled, wasAborted, onEdit, onHover }) => {
  const { t } = useTranslation();
  const matchesLgDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  const dispatch = useSharedDispatch();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setFilterParams] = useSearchParams();

  const cleaningZonesIdName = useSharedSelector((state) => selectAllCleaningZonesIdName(state));
  const landmarksIdName = useSharedSelector((state) => selectAllLandmarksIdName(state));
  const isWebApp = useSharedSelector((state) => selectIsWebApp(state));

  const { deleteMissionById, sendRobotWebsocketMessage } = useSharedActions();

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const editMissionClickHandler = () => {
    onEdit(mission);
  };

  const deleteMissionClickHandler = () => {
    setIsConfirmDeleteOpen(true);
  };

  const confirmDeleteClickHandler = () => {
    dispatch(deleteMissionById(mission.id));
  };

  const abortDeleteClickHandler = () => {
    setIsConfirmDeleteOpen(false);
  };

  const hoverSectionsHandler = () => {
    onHover([...mission.selectedLandmarkIds, ...mission.selectedZoneIds]);
  };

  const mouseOutHandler = () => {
    onHover([]);
  };

  const startMissionCleaningClickHandler = () => {
    const cleaningCommandMsg = getDefaultCleaningCommandButtonSender({ missionId: mission.id });
    dispatch(sendRobotWebsocketMessage({ type: MessageType.Actions, message: cleaningCommandMsg }));
    if (isWebApp) {
      navigate(WebAppNavigationRoutes.routeDashboard);
    } else {
      setFilterParams((prevState) => {
        prevState.set(FilterKeys.Tab, SphereRobotTabs.Dashboard);
        return prevState;
      });
    }
  };

  const scheduleMissionClickHandler = () => {
    if (isWebApp) {
      navigate(`/scheduler?${FilterKeys.MissionId}=${mission.id}`);
    } else {
      setFilterParams((prevState) => {
        prevState.set(FilterKeys.Tab, SphereRobotTabs.Scheduler);
        prevState.set(FilterKeys.MissionId, mission.id);
        return prevState;
      });
    }
  };

  return (
    <React.Fragment>
      <Tooltip title={wasAborted ? t("info-mission-aborted") : undefined}>
        <Card
          onMouseOver={matchesLgDown ? undefined : hoverSectionsHandler}
          onMouseOut={matchesLgDown ? undefined : mouseOutHandler}
          sx={(theme) => ({
            borderLeft: `${theme.spacing(1)} ${getColorFromString(mission.color, "0.3")} solid`,
            boxShadow: wasAborted ? `0 0 ${theme.spacing(1)} ${theme.palette.warning.main}` : undefined,
            ":before": {
              backgroundColor: "red",
              width: "100%",
              height: "100%",
              content: "''",
            },
          })}
        >
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: undefined, sm: "center" }}
              justifyContent="space-between"
              sx={{ overflow: "hidden" }}
            >
              <Stack flexGrow={1} sx={{ overflow: "hidden" }}>
                <Typography textOverflow="ellipsis" overflow="hidden">
                  {mission.name}
                </Typography>
                <Stack spacing={2} alignItems="center" direction="row">
                  <Typography variant="subtitle2">{getNavigatorDescription(mission.mode)}</Typography>
                  {(mission.selectedLandmarkIds.length > 0 || mission.selectedZoneIds.length > 0) && (
                    <Tooltip
                      title={
                        <Stack>
                          {mission.selectedLandmarkIds.map((lm) => (
                            <Typography key={lm}>{landmarksIdName.find((l) => l.id === lm)?.name}</Typography>
                          ))}
                          {mission.selectedZoneIds.map((z) => (
                            <Typography key={z}>{cleaningZonesIdName.find((cz) => cz.id === z)?.name}</Typography>
                          ))}
                        </Stack>
                      }
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PlaceOutlinedIcon fontSize="small" />
                        <Typography variant="subtitle2">
                          {mission.selectedLandmarkIds.length > 0
                            ? t("info-mission-landmarks")
                            : t("info-mission-zones")}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexShrink={0}>
                {matchesLgDown && (mission.selectedLandmarkIds.length > 0 || mission.selectedZoneIds.length > 0) && (
                  <ReadOnlyProtectedIconButton onClick={hoverSectionsHandler}>
                    <MapIcon />
                  </ReadOnlyProtectedIconButton>
                )}
                <Tooltip title={cleaningActionEnabled ? undefined : t("tooltip-cleaning-action-disabled")}>
                  <span>
                    <ReadOnlyProtectedIconButton
                      onClick={startMissionCleaningClickHandler}
                      disabled={!cleaningActionEnabled}
                    >
                      <PlayCircleFilledWhiteOutlinedIcon />
                    </ReadOnlyProtectedIconButton>
                  </span>
                </Tooltip>
                <ReadOnlyProtectedIconButton onClick={scheduleMissionClickHandler}>
                  <TodayRoundedIcon />
                </ReadOnlyProtectedIconButton>
                <ReadOnlyProtectedIconButton onClick={editMissionClickHandler} color="success">
                  <EditRoundedIcon />
                </ReadOnlyProtectedIconButton>
                <ReadOnlyProtectedIconButton onClick={deleteMissionClickHandler} color="error">
                  <DeleteOutlineRoundedIcon />
                </ReadOnlyProtectedIconButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Tooltip>
      <ConfirmAbortDialog
        isOpen={isConfirmDeleteOpen}
        title={t("confirm-delete-mission-title")}
        description={t("confirm-delete-mission-description")}
        abortButtonText={t("button-cancel")}
        confirmButtonText={t("button-confirm")}
        onConfirm={confirmDeleteClickHandler}
        onAbort={abortDeleteClickHandler}
      />
    </React.Fragment>
  );
};

export default MissionItem;
